import React from 'react';
import {t} from '@lingui/core/macro';

import Table, {IColumn} from 'shared/ui/Table/Table';
import Button from 'shared/ui/Button/Button/Button';
import {useCreateGate} from 'entities/tariff';
import {ExceededModal} from 'entities/user';
import Plus from 'shared/ui/icons/Plus';
import ServerAlertRuleRow from 'features/server/ui/ServerAlertRule/ServerAlertRuleRow/ServerAlertRuleRow';
import ServerAlertRuleModal from 'features/server/ui/ServerAlertRule/ServerAlertRuleModal';
import useServerAlertRules from 'features/server/model/useServerAlertRules';
import IServerOutput from 'features/server/model/Server/IServer';

import styles from './ServerAlertRules.module.scss';

interface IServerAlertRulesProps {
    server: IServerOutput,
}

const ServerAlertRules: React.FC<IServerAlertRulesProps> = ({server}) => {
    const {
        items, firstCommonUuid, firstOwnUuid, enabledCount, rulesLimit, metricOptions, isLoading, canCreate,
    } = useServerAlertRules(server);
    const {open: handleCreate, close: closeCreateGate, isCreateOpen, isExceededOpen} = useCreateGate(canCreate);

    const columns: IColumn[] = [
        {label: t`Метрика`},
        {label: t`Условие`},
        {label: t`Нарушение`},
        {label: t`Восстановление`},
        {label: t`Важность`},
        {label: t`Включено`},
        {label: ''},
    ];

    const renderGroupRow = (title: string, hint: string) => (
        <tr className={styles.groupRow}>
            <td colSpan={columns.length}>
                <span className={styles.groupTitle}>{title}</span>
                <span className={styles.groupHint}>{hint}</span>
            </td>
        </tr>
    );

    return (
        <div className={styles.section}>
            <div className={styles.header}>
                <h3 className={styles.sectionTitle}>
                    {t`Правила уведомлений`}: {t`${enabledCount} из ${rulesLimit}`}
                </h3>
                <Button variant="gradient" onClick={handleCreate} className={styles.createButton}>
                    <Plus width={24} height={24}/>
                    {t`Добавить правило`}
                </Button>
            </div>

            {
                !isLoading && items.length === 0
                    ? <div className={styles.empty}>{t`Правила для этого сервера ещё не настроены`}</div>
                    : (
                        <Table
                            columns={columns}
                            items={items}
                            rowFactory={(item) => (
                                <React.Fragment key={item.uuid}>
                                    {
                                        item.uuid === firstCommonUuid && renderGroupRow(
                                            t`Общие правила`,
                                            t`Действуют на все серверы аккаунта`,
                                        )
                                    }
                                    {
                                        item.uuid === firstOwnUuid && renderGroupRow(
                                            t`Правила этого сервера`,
                                            t`Действуют только на ${server.name}`,
                                        )
                                    }
                                    <ServerAlertRuleRow
                                        item={item}
                                        metricOptions={metricOptions}
                                        isCommon={item.server === null}
                                    />
                                </React.Fragment>
                            )}
                            isLoading={isLoading}
                            className={styles.table}
                            emptyStateComponent
                        />
                    )
            }

            <ServerAlertRuleModal
                open={isCreateOpen}
                presetServerUuid={server.uuid}
                handleClose={closeCreateGate}
            />

            <ExceededModal
                isOpen={isExceededOpen}
                handleClose={closeCreateGate}
                type="alert_rules"
            />
        </div>
    );
};

export default ServerAlertRules;
